import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTwoDaysPolicy } from '@repo/common';
import PolicyPage from '../Policy/PolicyPage';
import { LangToggle } from './TwoDaysApply';
import { getPolicyVersion, getPolicyVersions } from '../../api/policy/policy.api';

/**
 * 2days project 이용약관 (/2days/terms) · 개인정보처리방침 (/2days/privacy).
 * 국문이 기본이고, 신청서처럼 위쪽 토글로 영문(?lang=en)을 본다. 두 언어가 다르면 국문이 우선한다.
 * 어드민이 Skills 에서 등록한 버전은 시행일에 자동으로 바뀌고, 이전 버전은 ?version= 으로 본다.
 */
const TwoDaysPolicy = ({ doc }) => {
  const [params, setParams] = useSearchParams();
  const requestedLang = params.get('lang') === 'en' ? 'en' : 'ko';
  const version = params.get('version');
  const policy = useTwoDaysPolicy({
    doc,
    version,
    lang: requestedLang,
    fetchList: getPolicyVersions,
    fetchVersion: getPolicyVersion,
  });
  const { lang, selected, current, document } = policy;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc, lang, version]);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    if (policy.notFound) updateParam('version', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy.notFound]);

  const isUpcoming = !!selected && selected.effectiveDate > policy.today;

  return (
    <div lang={lang} className='min-h-screen bg-grayscale-gray00'>
      <div className='sticky top-0 z-10 flex justify-center border-b border-green-deep03/[0.08] bg-grayscale-gray00/95 backdrop-blur'>
        <div className='flex w-full max-w-[768px] items-center justify-between gap-3 px-6 py-3'>
          <span className='text-[13px] font-medium text-green-deep03'>2days project</span>
          <div className='flex items-center gap-3'>
            {policy.all.length > 1 && selected && (
              <select
                aria-label={lang === 'ko' ? '시행일' : 'Effective date'}
                value={selected.effectiveDate}
                onChange={(e) =>
                  updateParam(
                    'version',
                    e.target.value === current.effectiveDate ? null : e.target.value,
                  )
                }
                className='h-8 rounded-full border border-grayscale-gray02 bg-grayscale-gray00 px-3 text-[13px] text-text-primary outline-none'
              >
                {[...policy.all].reverse().map((item) => (
                  <option key={item.effectiveDate} value={item.effectiveDate}>
                    {item.effectiveDate.replace(/-/g, '.')}
                    {item === current
                      ? lang === 'ko'
                        ? ' (현재 시행)'
                        : ' (current)'
                      : item.effectiveDate > policy.today
                        ? lang === 'ko'
                          ? ' (시행 예정)'
                          : ' (upcoming)'
                        : ''}
                  </option>
                ))}
              </select>
            )}
            <LangToggle lang={lang} onChange={(next) => updateParam('lang', next === 'en' ? 'en' : null)} />
          </div>
        </div>
      </div>
      {policy.loaded && policy.documentLoaded && document && (
        <PolicyPage
          title={document.title}
          lastUpdated={document.effective}
          lastUpdatedLabel={
            isUpcoming ? (lang === 'ko' ? '시행 예정일' : 'Takes effect') : document.effectiveLabel
          }
          sections={[{ heading: '', body: document.notes }, ...document.sections]}
        />
      )}
    </div>
  );
};

export default TwoDaysPolicy;
